import { reduce } from 'ramda'

const orgColumnWidth = 200 as const

export enum ColumnKey {
  Org = 'org',
  OrgGhost = 'orgGhost',
  ParentsExpenses = 'parentsExpenses',
  BudgetExpencesFrom1To11 = 'budgetExpencesFrom1To11',
  BudgetExpencesFrom1To11Total = 'budgetExpencesFrom1To11Total',
  BudgetExpencesFrom1To4 = 'budgetExpencesFrom1To4',
  Total = 'total',
}

export const createDinamicColumnKey = (allowance: string) => {
  return `budgetExpencesFrom1To11_${allowance.replace(/\s/g, '_')}`
}

export const columnKeysLeft = [ColumnKey.Org]
export const createColumnKeysLeftHeaders = (hasAllowances: boolean) => {
  return hasAllowances ? [columnKeysLeft, [ColumnKey.OrgGhost]] : [columnKeysLeft]
}

export const createColumnKeysMain = (allowanceIDs: Array<string>, hasAllowances: boolean) => {
  return [
    ColumnKey.ParentsExpenses,
    ...(hasAllowances ? allowanceIDs.map(createDinamicColumnKey) : []),
    hasAllowances ? ColumnKey.BudgetExpencesFrom1To11Total : ColumnKey.BudgetExpencesFrom1To11,
    ColumnKey.BudgetExpencesFrom1To4,
    ColumnKey.Total,
  ]
}

export const createColumnKeysMainHeaders = (
  allowanceIDs: Array<string>,
  hasAllowances: boolean,
) => {
  return hasAllowances
    ? [
        [
          ColumnKey.ParentsExpenses,
          ColumnKey.BudgetExpencesFrom1To11,
          ColumnKey.BudgetExpencesFrom1To4,
          ColumnKey.Total,
        ],
        [...allowanceIDs.map(createDinamicColumnKey), ColumnKey.BudgetExpencesFrom1To11Total],
      ]
    : [
        [
          ColumnKey.ParentsExpenses,
          ColumnKey.BudgetExpencesFrom1To11,
          ColumnKey.BudgetExpencesFrom1To4,
          ColumnKey.Total,
        ],
      ]
}

const headerCells = {
  [ColumnKey.Org]: 'Заведение',
  [ColumnKey.OrgGhost]: '',
  [ColumnKey.ParentsExpenses]: 'Питание за родительские средства',
  [ColumnKey.BudgetExpencesFrom1To11]: 'Питание за бюджетные средства 1–11 классы',
  [ColumnKey.BudgetExpencesFrom1To11Total]: 'ИТОГО',
  [ColumnKey.BudgetExpencesFrom1To4]: 'Питание за бюджетные средства 1–4 классы',
  [ColumnKey.Total]: 'ВСЕГО',
}

export const createHeaderCells = (allowanceIDs: Array<string>) => {
  return reduce(
    (acc, allowance) => {
      const key = createDinamicColumnKey(allowance)
      acc[key] = allowance
      return acc
    },
    headerCells,
    allowanceIDs,
  )
}

const getHeaderCellsProps = (allowancesCount: number) => {
  const rowSpan = !!allowancesCount ? 2 : 1
  const colSpan = !!allowancesCount ? allowancesCount + 1 : 1
  return {
    [ColumnKey.Org]: { alignLeft: true, width: orgColumnWidth, rowSpan, bgFilled: true },
    [ColumnKey.OrgGhost]: { alignLeft: true, width: 0, stripPadding: true, bgFilled: true },
    [ColumnKey.ParentsExpenses]: {
      alignRight: true,
      rowSpan,
      borderRight: true,
      ignoreFirstChild: true,
      bgFilled: true,
    },
    [ColumnKey.BudgetExpencesFrom1To11]: {
      alignRight: true,
      borderRight: true,
      colSpan,
      bgFilled: true,
    },
    [ColumnKey.BudgetExpencesFrom1To11Total]: {
      alignRight: true,
      borderRight: true,
      ignoreLastChild: true,
      bgFilled: true,
    },
    [ColumnKey.BudgetExpencesFrom1To4]: {
      alignRight: true,
      rowSpan,
      borderRight: true,
      bgFilled: true,
    },
    [ColumnKey.Total]: { alignRight: true, rowSpan, bgFilled: true },
  }
}

export const createHeaderCellsProps = (allowanceIDs: Array<string>, allowancesCount: number) => {
  let index = 0
  return reduce(
    (acc, allowance) => {
      const key = createDinamicColumnKey(allowance)
      acc[key] = { alignRight: true, ignoreFirstChild: !index, bgFilled: true }
      index += 1
      return acc
    },
    getHeaderCellsProps(allowancesCount),
    allowanceIDs,
  )
}

const cellsProps = {
  [ColumnKey.Org]: { alignLeft: true, width: orgColumnWidth },
  [ColumnKey.ParentsExpenses]: { alignRight: true, borderRight: true, ignoreFirstChild: true },
  [ColumnKey.BudgetExpencesFrom1To11]: { alignRight: true, borderRight: true },
  [ColumnKey.BudgetExpencesFrom1To11Total]: { alignRight: true, borderRight: true },
  [ColumnKey.BudgetExpencesFrom1To4]: { alignRight: true, borderRight: true },
  [ColumnKey.Total]: { alignRight: true },
}

export const createCellsProps = (allowanceIDs: Array<string>) => {
  return reduce(
    (acc, allowance) => {
      const key = createDinamicColumnKey(allowance)
      acc[key] = { alignRight: true }
      return acc
    },
    cellsProps,
    allowanceIDs,
  )
}

const totalCellsProps = {
  [ColumnKey.Org]: { alignRight: true, width: orgColumnWidth, bold: true },
  [ColumnKey.ParentsExpenses]: {
    alignRight: true,
    highlighted: true,
    bold: true,
    ignoreFirstChild: true,
    borderRight: true,
  },
  [ColumnKey.BudgetExpencesFrom1To11]: {
    alignRight: true,
    highlighted: true,
    borderRight: true,
    bold: true,
  },
  [ColumnKey.BudgetExpencesFrom1To11Total]: {
    alignRight: true,
    highlighted: true,
    borderRight: true,
    bold: true,
  },
  [ColumnKey.BudgetExpencesFrom1To4]: {
    alignRight: true,
    highlighted: true,
    borderRight: true,
    bold: true,
  },
  [ColumnKey.Total]: { alignRight: true, highlighted: true, bold: true },
}

export const createTotalCellProps = (allowanceIDs: Array<string>) => {
  return reduce(
    (acc, allowance) => {
      const key = createDinamicColumnKey(allowance)
      acc[key] = { alignRight: true, highlighted: true, bold: true }
      return acc
    },
    totalCellsProps,
    allowanceIDs,
  )
}
